/**
 * InvestorDocsPanel
 *
 * VBB-operator-only panel that lists the markdown docs under /app/documents/
 * (YC prep, SAFE drafts, market research, legal templates, articles) with
 * inline markdown preview + raw download.
 *
 * Mounted inside /ops → Documents tab.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FileText, Eye, Download, Loader2, FolderOpen, FileDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { authedDownload } from '../lib/authedDownload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Operations: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  Investors: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  Legal: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  Research: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  Articles: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  Partners: 'bg-pink-500/10 text-pink-300 border-pink-500/30',
  General: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
};

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocDate(ts) {
  // Backend returns epoch seconds (float). Guard against undefined/null.
  if (!ts && ts !== 0) return null;
  try {
    const d = new Date(ts * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

export default function InvestorDocsPanel() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preparedFor, setPreparedFor] = useState('');
  const [bundleBuilding, setBundleBuilding] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/investor-docs`, { withCredentials: true })
      .then((res) => setCategories(res.data.categories || []))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load documents');
      })
      .finally(() => setLoading(false));
  }, []);

  const openPreview = useCallback(async (doc) => {
    setActiveDoc(doc);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewContent('');
    try {
      const res = await axios.get(`${API}/investor-docs/content`, {
        params: { path: doc.path },
        withCredentials: true,
      });
      setPreviewContent(typeof res.data === 'string' ? res.data : String(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load document');
      setPreviewContent(`# Failed to load\n\n${err.response?.data?.detail || err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const downloadDoc = useCallback((doc) => {
    authedDownload(
      `/investor-docs/download?path=${encodeURIComponent(doc.path)}`,
      doc.filename,
      token,
    );
  }, [token]);

  const downloadPdf = useCallback((doc) => {
    toast.info('Generating PDF...');
    authedDownload(
      `/investor-docs/pdf?path=${encodeURIComponent(doc.path)}`,
      doc.filename.replace(/\.md$/, '.pdf'),
      token,
    );
  }, [token]);

  const downloadBundle = useCallback(async () => {
    const trimmed = preparedFor.trim();
    const qs = trimmed ? `?prepared_for=${encodeURIComponent(trimmed)}` : '';
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    setBundleBuilding(true);
    toast.info(`Building ZIP${trimmed ? ` for ${trimmed}` : ''}. This can take 15-30 seconds…`);
    try {
      await authedDownload(`/investor-docs/pdf-bundle${qs}`, `aadc-docs-all-${ts}.zip`, token);
    } finally {
      setBundleBuilding(false);
    }
  }, [token, preparedFor]);

  const downloadCategoryBundle = useCallback(async (categoryPath) => {
    const trimmed = preparedFor.trim();
    const params = new URLSearchParams({ category: categoryPath });
    if (trimmed) params.set('prepared_for', trimmed);
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const safeSlug = categoryPath.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    setBundleBuilding(true);
    toast.info(`Building ${categoryPath} ZIP${trimmed ? ` for ${trimmed}` : ''}…`);
    try {
      await authedDownload(
        `/investor-docs/pdf-bundle?${params.toString()}`,
        `aadc-${safeSlug}-${ts}.zip`,
        token,
      );
    } finally {
      setBundleBuilding(false);
    }
  }, [token, preparedFor]);

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading documents...
        </CardContent>
      </Card>
    );
  }

  const totalDocs = categories.reduce((sum, c) => sum + c.docs.length, 0);

  return (
    <>
      <Card className="bg-slate-800/50 border-slate-700" data-testid="investor-docs-panel">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 flex-wrap">
            <FolderOpen className="w-5 h-5 text-amber-400" />
            Investor & Operator Documents
            <span className="text-sm font-normal text-slate-400 ml-2">
              {totalDocs} docs across {categories.length} categories
            </span>
            <div className="ml-auto flex items-center gap-2">
              <input
                type="text"
                value={preparedFor}
                onChange={(e) => setPreparedFor(e.target.value)}
                maxLength={80}
                placeholder="Prepared for... (optional)"
                className="h-7 px-2 text-xs rounded border border-slate-600 bg-slate-900 text-slate-200 placeholder-slate-500 w-48 focus:outline-none focus:border-amber-500"
                data-testid="prepared-for-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={downloadBundle}
                disabled={bundleBuilding}
                className="h-7 px-3 text-xs border-amber-500/50 text-amber-300 hover:bg-amber-500/10 whitespace-nowrap disabled:opacity-60"
                data-testid="download-all-pdfs-btn"
                title="Download every doc as a ZIP of PDFs with a cover page"
              >
                {bundleBuilding ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Building ZIP…
                  </>
                ) : (
                  <>
                    <FileDown className="w-3 h-3 mr-1" /> Download all as PDFs
                  </>
                )}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {categories.length === 0 && (
            <div className="text-slate-400 text-sm">No documents found under /app/documents/.</div>
          )}
          {categories.map((cat) => (
            <div key={cat.name} data-testid={`docs-category-${cat.name}`}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider">
                  {cat.name}
                </h3>
                <Badge
                  variant="outline"
                  className={CATEGORY_COLORS[cat.name] || CATEGORY_COLORS.General}
                >
                  {cat.docs.length}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadCategoryBundle(cat.name)}
                  disabled={bundleBuilding}
                  className="ml-auto h-6 px-2 text-xs text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 disabled:opacity-60"
                  data-testid={`download-category-${cat.name}`}
                  title={`Download all ${cat.name} docs as a ZIP of PDFs`}
                >
                  <FileDown className="w-3 h-3 mr-1" /> ZIP
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {cat.docs.map((doc) => (
                  <div
                    key={doc.path}
                    className="p-3 bg-slate-700/40 rounded-lg border border-slate-700 hover:bg-slate-700/60 transition-colors"
                    data-testid={`doc-item-${doc.filename}`}
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate" title={doc.title}>
                          {doc.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {doc.filename} · {formatBytes(doc.size)}
                        </div>
                        {(doc.ctime || doc.mtime) && (
                          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            {doc.ctime && (
                              <span title="Date this file first appeared under /app/documents/">
                                Added {formatDocDate(doc.ctime)}
                              </span>
                            )}
                            {doc.mtime && doc.ctime && Math.abs(doc.mtime - doc.ctime) > 60 && (
                              <>
                                <span className="text-slate-700">·</span>
                                <span title="Date this file was last modified">
                                  Updated {formatDocDate(doc.mtime)}
                                </span>
                              </>
                            )}
                            {doc.mtime && !doc.ctime && (
                              <span>Updated {formatDocDate(doc.mtime)}</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPreview(doc)}
                            className="h-7 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-700"
                            data-testid={`doc-preview-${doc.filename}`}
                          >
                            <Eye className="w-3 h-3 mr-1" /> Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDoc(doc)}
                            className="h-7 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-700"
                            data-testid={`doc-download-${doc.filename}`}
                          >
                            <Download className="w-3 h-3 mr-1" /> .md
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPdf(doc)}
                            className="h-7 px-2 text-xs border-slate-600 text-slate-200 hover:bg-slate-700"
                            data-testid={`doc-pdf-${doc.filename}`}
                          >
                            <FileDown className="w-3 h-3 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-y-auto"
          data-testid="doc-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <FileText className="w-5 h-5 text-amber-500" />
              {activeDoc?.title || 'Document'}
            </DialogTitle>
            {activeDoc && (
              <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                <span>{activeDoc.path}</span>
                <span>·</span>
                <span>{formatBytes(activeDoc.size)}</span>
                {activeDoc.ctime && (
                  <>
                    <span>·</span>
                    <span>Added {formatDocDate(activeDoc.ctime)}</span>
                  </>
                )}
                {activeDoc.mtime && activeDoc.ctime && Math.abs(activeDoc.mtime - activeDoc.ctime) > 60 && (
                  <>
                    <span>·</span>
                    <span>Updated {formatDocDate(activeDoc.mtime)}</span>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadDoc(activeDoc)}
                  className="ml-auto h-6 px-2 text-xs"
                  data-testid="doc-preview-download-md"
                >
                  <Download className="w-3 h-3 mr-1" /> Download .md
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPdf(activeDoc)}
                  className="h-6 px-2 text-xs"
                  data-testid="doc-preview-download-pdf"
                >
                  <FileDown className="w-3 h-3 mr-1" /> Download PDF
                </Button>
              </div>
            )}
          </DialogHeader>
          <div className="mt-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
              </div>
            ) : (
              <div
                className="prose prose-sm max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5"
                data-testid="doc-preview-content"
              >
                <ReactMarkdown>{previewContent}</ReactMarkdown>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
